'use client';

import { tracking } from '@/lib/features/tracking';
import { useCallback, useRef, useState } from 'react';
import type { UploadedFile } from '../props';
import type { ExtractedDocument, ExtractionProgress } from '../types';
import { extractDocumentText } from '../utils/document-extraction';

export function useFileUpload() {
    const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
    const [extractedDocument, setExtractedDocument] = useState<ExtractedDocument | null>(null);
    const [isExtracting, setIsExtracting] = useState(false);
    const [extractionProgress, setExtractionProgress] = useState<ExtractionProgress | null>(null);
    const uploadIdRef = useRef(0);

    const upload = useCallback(async (file: File): Promise<{ hasError: boolean }> => {
        const id = ++uploadIdRef.current;

        setUploadedFile({ file, name: file.name });
        setExtractedDocument(null);
        setIsExtracting(true);
        setExtractionProgress({ stage: 'reading', progress: 0, message: 'Starting...' });

        try {
            const extracted = await extractDocumentText(file, (progress) => {
                if (uploadIdRef.current === id) {
                    setExtractionProgress(progress);
                }
            });
            if (uploadIdRef.current !== id) return { hasError: false };
            setExtractedDocument(extracted);
            setExtractionProgress({ stage: 'complete', progress: 100, message: 'Ready' });
            return { hasError: false };
        } catch (error) {
            if (uploadIdRef.current !== id) return { hasError: true };
            tracking.errorWithSource(error as Error, 'document-extraction-failed');
            setExtractedDocument(null);
            setExtractionProgress(null);
            setUploadedFile(null);
            return { hasError: true };
        } finally {
            if (uploadIdRef.current === id) {
                setIsExtracting(false);
            }
        }
    }, []);

    const remove = useCallback(() => {
        setUploadedFile(null);
        setExtractedDocument(null);
        setExtractionProgress(null);
    }, []);

    return {
        uploadedFile,
        extractedDocument,
        isExtracting,
        extractionProgress,
        upload,
        remove,
    };
}
